import React, { useState, useEffect } from 'react';

const sections = [
  { id: 'introduction', title: 'Informations générales' },
  { id: 'legal-basis', title: 'Base juridique du traitement' },
  { id: 'data-collection', title: 'Données collectées et finalités' },
  { id: 'data-sharing', title: 'Sous-traitants et destinataires' },
  { id: 'international-transfers', title: 'Transferts internationaux' },
  { id: 'data-retention', title: 'Durées de conservation' },
  { id: 'user-rights', title: 'Droits des utilisateurs' },
  { id: 'mobile-app', title: 'Spécificités App Store / Google Play' },
  { id: 'data-security', title: 'Sécurité des données' },
  { id: 'cookies', title: 'Cookies' },
  { id: 'minors', title: 'Mineurs' },
  { id: 'contact', title: 'Contact officiel' },
  { id: 'policy-changes', title: 'Modifications de la déclaration' },
];

const PrivacyPolicy: React.FC = () => {
  const [showScrollTop, setShowScrollTop] = useState(false);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="px-4 py-10 sm:py-12 max-w-4xl mx-auto">
      <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
        Politique de Confidentialité
      </h1>
      <p className="text-gray-600 mt-2">
        Dernière mise à jour : 5 septembre 2025
      </p>

      {/* Table of contents */}
      <nav
        aria-label="Table of contents"
        className="mt-6 rounded-lg border border-gray-200 bg-white p-4"
      >
        <h2 className="text-sm font-semibold text-gray-900 mb-3">Sommaire</h2>
        <ol className="list-inside space-y-1 text-sm">
          {sections.map((s, idx) => (
            <li key={s.id}>
              <button
                onClick={(e) => {
                  e.preventDefault();
                  scrollToSection(s.id);
                }}
                className="text-indigo-600 hover:text-indigo-800 text-left cursor-pointer bg-transparent border-none p-0 underline"
              >
                {idx + 1}. {s.title}
              </button>
            </li>
          ))}
        </ol>
      </nav>

      {/* Details */}
      <div className="mt-8 space-y-12 text-gray-800 leading-relaxed">
        <section id="introduction" className="scroll-mt-24">
          <h3 className="text-lg font-semibold mb-4">1. Informations générales</h3>
          <div className="space-y-4">
            <p>
              La présente politique de confidentialité vous informe de la manière dont CVMatchswiss, 
              opéré par P-TRON TECH (ci-après « nous »), collecte et traite vos données personnelles dans 
              le cadre de ses services. En utilisant nos services (site web cvmatch.io, application 
              mobile, etc.), vous acceptez les modalités décrites ci-dessous concernant la protection de vos 
              données. Nous accordons une importance primordiale à la confidentialité et traitons vos 
              données personnelles conformément à la Loi fédérale sur la protection des données (LPD) 
              suisse et, le cas échéant, au Règlement général sur la protection des données (RGPD) de 
              l’Union européenne. En tant que responsable du traitement, nous nous engageons à respecter 
              strictement ces législations et à n’utiliser vos données que pour des finalités légitimes, de 
              manière proportionnée et sécurisée.
            </p>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <h4 className="font-semibold text-blue-900 mb-2">Responsable du traitement</h4>
              <p className="text-blue-800 text-sm">
                Notre service est opéré par P-TRON TECH, société tech suisse enregistrée en Suisse. 
                À ce titre, P-TRON TECH est le responsable du traitement de vos données personnelles 
                collectées via notre plateforme. Nous traitons toutes les données personnelles qui nous 
                sont confiées avec la plus grande confidentialité, uniquement pour les finalités communiquées 
                et dans les limites nécessaires à ces finalités. Si vous avez des questions sur le traitement 
                de vos données ou souhaitez exercer vos droits (détaillés en section 7), vous pouvez nous 
                contacter aux coordonnées indiquées à la section 12.
              </p>
            </div>
            <p>
              <strong>Champ d'application :</strong> Cette politique de confidentialité s'applique à l'ensemble de nos 
              services, y compris l'utilisation de notre site internet et de notre application mobile. Elle 
              couvre les données personnelles des utilisateurs candidats (personnes utilisant notre 
              plateforme pour optimiser leur CV, générer des lettres de motivation, etc.). Elle ne s'adresse 
              pas aux recruteurs ou employeurs (qui ne sont pas utilisateurs dans le cadre du service offert 
              aux candidats). Nos conditions générales d'utilisation complètent la présente déclaration sur 
              les aspects contractuels non relatifs à la vie privée.
            </p>
            <p>
              <strong>Définitions :</strong> Par « données personnelles », nous entendons toute information se rapportant à 
              une personne physique identifiée ou identifiable (par ex. nom, adresse e-mail, numéro de 
              téléphone, informations de CV, etc.). Le terme « traitement » désigne toute opération sur ces 
              données (collecte, consultation, utilisation, conservation, transmission, effacement, etc.). 
              Nous utilisons le terme « utilisateur » pour désigner toute personne utilisant nos services.
            </p>
            <p>
              <strong>Données de tiers :</strong> Il est possible que vous nous communiquiez des informations concernant 
              des tiers dans le cadre de l’utilisation de nos services (par ex. les coordonnées d’une personne 
              de référence présentes sur votre CV). Si vous choisissez de partager des données personnelles 
              de tiers, vous devez vous assurer que vous êtes autorisé à le faire et, le cas échéant, que ces 
              personnes ont été informées de cette transmission. Nous traiterons les données de tiers 
              conformément à la présente politique, de la même manière que vos propres données.
            </p>
          </div>
        </section>

        <section id="legal-basis" className="scroll-mt-24">
          <h3 className="text-lg font-semibold mb-4">2. Base juridique du traitement</h3>
          <div className="space-y-4">
            <p>
              Nous traitons vos données personnelles uniquement si une base légale prévue par la LPD 
              suisse et/ou le RGPD s’applique. En pratique, selon la nature du traitement concerné, l’une ou 
              plusieurs des justifications juridiques suivantes seront utilisées :
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong>Exécution d’un contrat :</strong> La plupart des traitements réalisés par CVMatchSwiss sont 
                fondés sur la nécessité d’exécuter le contrat qui nous lie à vous. Lorsque vous créez un 
                compte et utilisez nos services (par ex. analyser votre CV, générer une lettre de 
                motivation), le traitement de vos données est nécessaire pour vous fournir ces services 
                conformément à nos Conditions Générales. Sans ces données, nous ne pourrions pas 
                remplir nos obligations contractuelles (création de votre profil, fourniture des résultats 
                d’analyse, assistance client, traitement des paiements, etc.).
              </li>
              <li>
                <strong>Consentement :</strong> Pour certains traitements spécifiques, nous pouvons vous demander 
                votre consentement préalable. Par exemple, si nous souhaitons utiliser vos données à 
                des fins de marketing (telles que l’envoi d’une newsletter ou d’offres 
                promotionnelles), ou si nous souhaitions traiter vos données au-delà des finalités 
                initiales, nous vous demanderons explicitement d’accepter ce traitement. Dans ce cas, 
                vous avez le droit de retirer votre consentement à tout moment (voir section 7), ce qui 
                mettra fin au traitement concerné.
              </li>
              <li>
                <strong>Intérêt légitime :</strong> Nous pouvons traiter certaines données sur la base de nos intérêts 
                légitimes, à condition que cela n’entraîne pas une atteinte disproportionnée à vos 
                droits et libertés. Par exemple, nous avons un intérêt légitime à améliorer nos services 
                en analysant l’utilisation qui en est faite, à prévenir la fraude et assurer la sécurité de 
                notre plateforme, ou à vous contacter pour vous proposer des services similaires à 
                ceux que vous avez déjà utilisés. Dans tous les cas, nous évaluons soigneusement si 
                notre intérêt légitime n’est pas supplanté par vos intérêts ou vos droits fondamentaux 
                en matière de protection des données. Vous avez le droit de vous opposer à tout 
                moment à un traitement fondé sur l’intérêt légitime pour des raisons tenant à votre 
                situation particulière (voir section 7).
              </li>
              <li>
                <strong>Obligation légale :</strong> Certains traitements de données peuvent être imposés par des 
                obligations légales auxquelles nous sommes soumis. Par exemple, les lois fiscales et 
                comptables peuvent nous obliger à conserver des données de facturation pendant une 
                certaine durée, ou nous pourrions devoir communiquer certaines informations aux 
                autorités en cas d’obligation légale (telle qu’une enquête judiciaire). Dans ces 
                situations, la base juridique de nos traitements est le respect d’une obligation légale.
              </li>
              <li>
                <strong>Intérêts vitaux ou mission d’intérêt public :</strong> Ces bases légales sont plus rares dans le 
                cadre de nos activités, mais si le traitement de données personnelles est nécessaire 
                pour protéger vos intérêts vitaux ou pour l’exécution d’une mission d’intérêt public, 
                nous pourrions nous appuyer sur de telles justifications. Par exemple, en cas d’urgence 
                sanitaire ou de menace grave et immédiate, le traitement de certaines données pourrait 
                être justifié pour protéger la vie ou l’intégrité des personnes. Dans la mesure du 
                possible, si un tel cas devait se présenter, nous vous informerions du traitement en 
                question et de sa base légale.
              </li>
            </ul>
            <p>
              En résumé, nous veillons à toujours disposer d’une base juridique valide pour traiter vos 
              données. Si nous souhaitions ultérieurement traiter vos données pour une finalité incompatible 
              avec celles initialement collectées, nous vous en informerions préalablement et, le cas 
              échéant, solliciterions votre consentement.
            </p>
          </div>
        </section>

        <section id="data-collection" className="scroll-mt-24">
          <h3 className="text-lg font-semibold mb-4">3. Données collectées et finalités</h3>
          <div className="space-y-6">
            <p>
              Nous collectons uniquement les données personnelles nécessaires aux finalités explicites 
              décrites ci-dessous. Le type de données recueillies dépend de votre interaction avec nos 
              services. Vous trouverez ci-après les catégories de données que nous collectons, 
              accompagnées de l’usage qui en est fait :
            </p>
            <div>
              <h4 className="font-semibold mb-2">Création de compte et identification</h4>
              <p>
                Lors de la création d’un compte CVMatch, nous 
                vous demandons des informations d’identification de base telles que votre nom, 
                prénom, adresse e-mail et un mot de passe. Ces données sont nécessaires pour créer et 
                gérer votre compte utilisateur, vous authentifier de manière sécurisée à chaque 
                connexion, et vous contacter au sujet de votre compte (par ex. pour la vérification de 
                votre adresse e-mail, l’envoi d’alertes de sécurité, ou des notifications importantes 
                liées au service). Sans ces informations, nous ne pouvons pas ouvrir de compte ni vous 
                donner accès aux fonctionnalités réservées aux utilisateurs enregistrés.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-2">Analyse de CV et d’offres d’emploi</h4>
              <p>
                Le cœur de notre service consiste à analyser 
                votre CV (curriculum vitae) ainsi que, le cas échéant, des offres d’emploi que vous 
                nous soumettez, afin de vous aider à les faire correspondre au mieux. À cette fin, nous 
                collectons les données que vous nous fournissez dans votre CV (telles que les 
                informations professionnelles et de formation, compétences, expériences, coordonnées 
                figurant sur le CV, etc.) ainsi que les données contenues dans l’offre d’emploi que 
                vous souhaitez cibler (intitulé du poste, description, compétences requises, etc.). Ces 
                données sont utilisées pour effectuer un matching automatisé et vous fournir une 
                analyse personnalisée : par exemple, identifier les mots-clés manquants dans votre CV 
                par rapport à l’offre, évaluer l’adéquation de votre profil au poste visé, et formuler des 
                recommandations pour optimiser votre CV en fonction du poste. Ce traitement est 
                effectué de manière confidentielle et vos documents (CV et offres) ne sont pas 
                partagés avec des tiers. Les résultats et suggestions fournis le sont uniquement à votre 
                attention.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-2">Génération de lettre de motivation</h4>
              <p>
                CVMatchSwiss vous offre la possibilité de 
                générer automatiquement une lettre de motivation adaptée à une offre d’emploi à 
                partir des informations de votre CV et des exigences du poste. Pour ce faire, nous 
                utilisons les données de votre profil (par ex. votre nom, intitulé de poste actuel, 
                compétences, expériences pertinentes) ainsi que les détails du poste visé (données de 
                l’offre d’emploi) et éventuellement des indications supplémentaires que vous 
                renseignez (par ex. vos motivations spécifiques, les points que vous souhaitez mettre 
                en avant). Un système algorithmique procède alors à la rédaction assistée d’une lettre 
                de motivation personnalisée. Important : ce processus est automatisé pour votre 
                confort, mais aucune décision n’est prise à votre sujet – vous restez libre de modifier 
                ou d’utiliser la lettre proposée à votre convenance. La génération automatisée 
                n’implique aucun profilage à effet juridique vous concernant. Les données que vous 
                fournissez pour la génération de la lettre sont utilisées uniquement pour produire le 
                document et vous le présenter. Elles ne sont pas réutilisées à d’autres fins sans votre 
                accord.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-2">Amélioration du service et développement</h4>
              <p>
                Afin d’améliorer continuellement nos 
                algorithmes de matching et de génération, ainsi que l’expérience utilisateur globale, 
                nous pouvons traiter certaines données relatives à votre utilisation. Cela inclut par 
                exemple : les fonctionnalités que vous utilisez et à quelle fréquence, les requêtes ou 
                documents que vous soumettez (CV, offres) et les retours que vous fournissez 
                (feedback, notes ou commentaires sur la qualité des suggestions). Ces informations 
                nous permettent de comprendre quelles parties du service sont les plus utiles, 
                d’identifier les éventuels problèmes ou points à améliorer, et de développer de 
                nouvelles fonctionnalités adaptées aux besoins des utilisateurs. Dans la mesure du 
                possible, nous analysons ces données de façon agrégée ou anonymisée (par exemple, 
                statistiques globales d’utilisation) de sorte qu’elles ne permettent pas de vous identifier 
                directement. Si nous souhaitons analyser plus en détail des contenus de CV ou de 
                lettres pour entraîner ou affiner nos modèles, nous le ferons soit avec des données 
                anonymisées, soit après avoir obtenu votre consentement explicite si les données ne 
                peuvent pas être suffisamment anonymisées. L’objectif général de ces traitements est 
                d’assurer la qualité, la fiabilité et l’innovation de nos services.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-2">Support client et communication</h4>
              <p>
                Lorsque vous nous contactez pour de l’assistance 
                (via un formulaire de contact, par e-mail à notre support, ou tout autre moyen mis à 
                disposition), nous collectons les informations que vous choisissez de nous 
                communiquer. Cela peut inclure votre nom, vos coordonnées de contact (adresse email, 
                numéro de téléphone) et le contenu de votre demande (par ex. description du 
                problème rencontré, captures d’écran, informations sur votre configuration). Ces 
                données sont utilisées uniquement pour vous identifier, comprendre et résoudre votre 
                problème, vous répondre et assurer le suivi de votre requête. Le contenu de vos 
                communications avec notre équipe support est traité de manière confidentielle. Il 
                pourra être utilisé pour améliorer le service (par ex. si vous signalez un bug ou 
                suggérez une amélioration, nous pouvons en tenir compte dans nos développements). 
                Si votre demande concerne l’exercice de vos droits en matière de données 
                personnelles, nous utiliserons les données fournies uniquement pour vérifier votre 
                identité (si nécessaire) et traiter votre requête conformément à la loi (voir section 7).
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-2">Paiement et facturation</h4>
              <p>
                Si vous souscrivez à des services payants (par exemple, un 
                abonnement Premium ou l’achat de crédits supplémentaires), nous traiterons des 
                données liées au paiement. Ces informations comprennent généralement vos données 
                de facturation (nom complet, adresse postale si requise pour la facturation, et 
                éventuellement numéro de TVA si vous êtes un professionnel), ainsi que les détails de 
                la transaction (montant, date et heure de paiement, type de service acheté). Note : Pour 
                votre sécurité, nous ne collectons ni ne stockons vos informations de paiement 
                sensibles (tel que le numéro de carte de crédit complet). Les paiements en ligne sont 
                gérés par un prestataire de paiement tiers certifié (par ex. un opérateur de paiement 
                comme Stripe, conforme PCI-DSS). Ce prestataire collecte vos coordonnées de 
                paiement directement via une interface sécurisée intégrée sur notre site/app, de sorte 
                que nous n’accédons jamais à l’intégralité de vos données de carte bancaire. Nous 
                recevons uniquement un accusé de paiement (succès/échec) et éventuellement 
                quelques informations partielles comme les 4 derniers chiffres de la carte ou un 
                identifiant de transaction, afin de lier le paiement à votre compte et activer les services 
                achetés. Les données de facturation que nous conservons servent à émettre des 
                reçus/factures et à respecter nos obligations légales comptables (voir section 6 sur la 
                durée de conservation).
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-2">Données techniques et de navigation</h4>
              <p>
                Lors de chacune de vos visites sur notre site 
                web ou utilisations de l’application mobile, certaines données sont collectées 
                automatiquement par nos systèmes ou par des cookies (voir section 10). Il s’agit par 
                exemple de votre adresse IP et de données associées (délai et date de connexion, 
                ville/pays approximatif déduit de l’IP), des informations sur votre appareil et 
                navigateur (type et version du navigateur, version de l’application, système 
                d’exploitation, modèle de l’appareil mobile), ainsi que des données de journalisation 
                (logs) sur les actions effectuées (par ex. accès aux différentes pages, clics sur les 
                fonctionnalités, erreurs éventuelles rencontrées). Nous utilisons ces informations 
                techniques pour assurer le bon fonctionnement et la sécurité de nos services (par ex. 
                prévenir les accès non autorisés, détecter les pannes ou tentatives d’attaques) et pour 
                mesurer l’audience de notre plateforme de manière globale. Ces données nous aident à 
                comprendre comment le site et l’app sont utilisés (quelles pages sont les plus 
                consultées, combien de temps les utilisateurs restent, etc.) et d’où provient notre 
                audience (sans pour autant procéder à un suivi individualisé cross-site). Lorsque ces 
                données de navigation ne sont pas strictement nécessaires au fonctionnement, nous les 
                traitons de préférence de façon anonymisée ou pseudonymisée. Par exemple, les 
                adresses IP peuvent être tronquées ou chiffrées pour l’analyse d’audience, de sorte 
                qu’elles ne permettent pas de vous identifier directement.
              </p>
            </div>
            <p>
              En résumé, nous collectons uniquement les données pertinentes pour vous offrir un service de 
              qualité, allant de l’inscription jusqu’à l’utilisation avancée de nos fonctionnalités 
              d’optimisation de candidature. À chaque collecte de données, nous veillons à vous informer 
              de la finalité poursuivie et à limiter l’utilisation des données à ce qui est nécessaire pour 
              atteindre ces finalités. Nous ne procéderons à aucun traitement ultérieur incompatible avec ces 
              objectifs sans vous en informer et, si requis, sans obtenir votre consentement au préalable.
            </p>
          </div>
        </section>

        <section id="data-sharing" className="scroll-mt-24">
          <h3 className="text-lg font-semibold mb-4">4. Sous-traitants et destinataires</h3>
          <div className="space-y-4">
            <p>
              Nous ne vendons ni ne louons vos données personnelles à des tiers. Cependant, pour la 
              réalisation de certaines prestations techniques ou support de notre activité, nous faisons appel 
              à des partenaires de confiance agissant en tant que sous-traitants (processors) ou, plus 
              rarement, en tant que destinataires de données. Ces tiers traitent vos données uniquement pour 
              notre compte et selon nos instructions, dans le but de nous aider à fournir le service. Nous 
              veillons à ce que chacun de nos sous-traitants présente des garanties suffisantes en matière de 
              protection des données (accords de confidentialité, mesures de sécurité, conformité aux lois 
              applicables). Voici les principales catégories de partenaires avec qui vos données peuvent être 
              partagées, ainsi que leur localisation :
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong>Hébergement et infrastructure cloud :</strong> Vos données sont stockées et traitées sur des 
                serveurs sécurisés mis à disposition par notre fournisseur d’hébergement, Amazon 
                Web Services (AWS). Nous utilisons de préférence des centres de données situés en 
                Suisse (lorsque disponible) ou dans l’Union européenne (p. ex. AWS région UE). Cela 
                garantit que vos données restent sous juridiction suisse ou européenne. AWS agit en 
                tant que sous-traitant technique pour le stockage et nous assure un haut niveau de 
                sécurité et de disponibilité.
              </li>
              <li>
                <strong>Base de données et backend :</strong> Nous pouvons recourir à des solutions de base de 
                données et de backend en tant que service, par exemple Supabase (plateforme de base 
                de données PostgreSQL gérée). Supabase agit en tant que sous-traitant pour héberger 
                la base de données contenant, entre autres, vos informations de compte, vos 
                documents (CV, lettres) et les résultats d’analyse. Là aussi, nous sélectionnons des 
                instances hébergées en Europe (Supabase propose l’hébergement sur des serveurs 
                européens), afin que les données ne quittent pas le territoire européen/suisse.
              </li>
              <li>
                <strong>Service de paiement :</strong> Comme indiqué en section 3, les paiements en ligne sont confiés 
                à un prestataire de paiement spécialisé (par ex. Stripe, Saferpay, ou équivalent). Ce 
                prestataire agit en tant que responsable de traitement pour les données de carte 
                bancaire (qu’il collecte directement) et en tant que sous-traitant pour nous fournir la 
                confirmation de paiement et faciliter la transaction. Le prestataire de paiement est 
                établi en Suisse ou dans l’UE (selon le service choisi) et conforme aux normes de 
                sécurité les plus strictes (certification PCI-DSS). Les données partagées avec lui se 
                limitent aux informations nécessaires au paiement (montant, identifiant de transaction, 
                votre nom/email pour relier au compte client, etc.).
              </li>
              <li>
                <strong>Services d'e-mail et notifications :</strong> Pour l'envoi des e-mails transactionnels 
                (confirmation de création de compte, réinitialisation de mot de passe, envoi de reçus 
                de paiement) ou des notifications push (sur l'application mobile), nous pouvons utiliser 
                des services tiers d'envoi d'e-mails ou de notifications. Par exemple, un service SMTP 
                sécurisé ou une API d'envoi d'e-mail (comme SendGrid ou Mailjet) peut être utilisé 
                pour délivrer les messages à votre adresse e-mail. De même, pour les notifications 
                push mobiles, nous utilisons les services fournis par Apple (APNs) pour iOS et notre 
                infrastructure AWS pour Android. Ces services ne reçoivent que les données 
                nécessaires (adresse e-mail, contenu du message ou identifiant de notification). Les 
                prestataires d'e-mailing choisis opèrent via des serveurs situés en Suisse/Europe ou 
                offrent des garanties adéquates si en dehors (voir section 5 sur les transferts 
                internationaux).
              </li>
              <li>
                <strong>Analytique et performance :</strong> Afin de comprendre l’utilisation de notre plateforme, 
                nous utilisons des outils d’analyse de trafic sans profilage intrusif. Nous privilégions 
                des solutions respectueuses de la vie privée (par ex. un logiciel d’analyse auto-hébergé 
                ou un service européen conforme). Si nous utilisons un outil tiers, il agit en tant que 
                sous-traitant pour collecter des données de fréquentation de manière anonymisée ou 
                pseudonymisée (p. ex. nombre de visites, parcours utilisateur). Aucune donnée 
                permettant de vous identifier directement (comme votre nom ou votre email) n’est 
                transmise à ces outils d’analyse. Si un outil comme Google Analytics venait à être 
                utilisé, nous configurerions des mesures de protection (anonymisation des IP, 
                désactivation du partage de données, etc.), et nous vous en informerions 
                spécifiquement dans la section Cookies. À ce jour, nous évitons tout traqueur 
                publicitaire ou profilage à des fins marketing de tiers, de sorte que vos données de 
                navigation ne sont pas monétisées.
              </li>
              <li>
                <strong>Support et maintenance :</strong> Il est possible que nous fassions appel à des sous-traitants 
                pour le support technique ou la maintenance de nos systèmes (par ex. une société 
                d’infogérance en Suisse/UE, ou un développeur freelance contractuel). Dans ce cas, 
                ces personnes peuvent avoir un accès limité à certaines données dans le seul but de 
                réaliser la tâche technique requise (par ex. analyser un bug sur la base de données). 
                Ces accès sont strictement contrôlés, temporaires, et font l’objet d’accords de 
                confidentialité rigoureux. Le personnel et les prestataires ayant accès aux données sont 
                soumis à une obligation légale et contractuelle de confidentialité.
              </li>
            </ul>
            <p>
              En dehors de ces cas, vos données personnelles ne sont pas communiquées à des tiers sans 
              votre consentement. Nous ne partageons jamais vos données avec des employeurs, des 
              plateformes d’emploi ou d’autres utilisateurs, sauf si vous nous le demandez explicitement 
              (par ex. si à l’avenir, CVMatchSwiss offre la possibilité d’envoyer directement une 
              candidature à une offre, cela ne se ferait qu’à votre initiative et avec votre accord explicite 
              pour chaque envoi).
            </p>
            <p>
              <strong>Exceptions légales :</strong> Nous pouvons être amenés à divulguer certaines de vos informations 
              personnelles à des tiers si la loi nous y oblige ou nous y autorise expressément. Par exemple, 
              en réponse à une ordonnance d’un tribunal ou une requête contraignante d’une autorité 
              publique (police, autorité de protection des données), ou pour faire valoir nos droits en justice, 
              prévenir un préjudice imminent ou enquêter sur une fraude. Dans de telles situations 
              exceptionnelles, nous ne partagerons que les données strictement nécessaires et vous 
              informerons de cette communication si la loi nous y autorise.
            </p>
            <p>
              <strong>Changement de structure de l’entreprise :</strong> Si CVMatchSwiss ou P-TRON TECH venait à faire 
              l’objet d’une fusion, acquisition, restructuration ou toute autre transaction impliquant le 
              transfert de tout ou partie de nos activités, les données personnelles des utilisateurs pourraient 
              être transmises aux entités impliquées dans la transaction (par ex. le nouvel exploitant du 
              service). Dans un tel cas, nous nous assurerions que vos données restent protégées par des 
              engagements de confidentialité au moins équivalents à ceux de la présente politique. Vous 
              seriez également informé d’un tel transfert de responsabilité et de toute modification 
              substantielle de la politique de confidentialité qui en résulterait.
            </p>
          </div>
        </section>

        <section id="international-transfers" className="scroll-mt-24">
          <h3 className="text-lg font-semibold mb-4">5. Transferts internationaux</h3>
          <div className="space-y-4">
            <p>
              <strong>Localisation des données :</strong> Par principe, nous privilégions le traitement et le stockage de vos 
              données personnelles sur le territoire suisse ou au sein de l’Espace économique européen 
              (UE/EEE). En effet, la Suisse et l’UE offrent un niveau de protection des données élevé et 
              comparable, et bénéficient de décisions d’adéquation réciproques. Vos données sont 
              majoritairement hébergées en Suisse ou en Europe (voir section 4 pour les détails par 
              prestataire). Cela signifie que, dans le cadre normal de nos opérations, vos données ne sortent 
              pas de ces zones protégées.
            </p>
            <p>
              <strong>Transfert en dehors de l’UE/Suisse :</strong> Dans certains cas spécifiques, il peut néanmoins être 
              nécessaire que certaines données soient transférées vers un pays tiers (c’est-à-dire un pays 
              situé en dehors de Suisse et de l’UE/EEE) – par exemple si nous avons un sous-traitant 
              technique ou un partenaire basé aux États-Unis ou dans un autre pays non couvert par une 
              décision d’adéquation. Dans une telle hypothèse, nous veillons à ce que des garanties 
              appropriées soient en place pour assurer une protection adéquate de vos données, 
              conformément aux exigences légales. Ces garanties peuvent inclure :
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>
                La signature de Clauses Contractuelles Types (CCT) de la Commission européenne 
                (ou accords équivalents approuvés par les autorités suisses) avec le destinataire des 
                données. Ces clauses contractuelles standard imposent au récipiendaire situé dans le 
                pays tiers des obligations strictes en matière de protection de vos informations, 
                équivalentes à celles prévues par le RGPD.
              </li>
              <li>
                Le recours à un organisme certifié dans le cadre d’un mécanisme de protection 
                reconnu. Par exemple, pour les transferts vers les États-Unis, si le destinataire est 
                certifié selon le Data Privacy Framework (nouveau cadre de protection des données 
                UE-USA et Suisse-USA), il est considéré comme offrant un niveau de protection 
                adéquat par les autorités. En date du 15 septembre 2024, le gouvernement suisse a 
                ajouté les États-Unis à la liste des États assurant une protection adéquate dès lors que 
                les entreprises réceptrices sont certifiées au programme Swiss-US Data Privacy 
                Frameworknews.admin.ch. Ainsi, si nous transférons des données à un prestataire 
                américain participant à ce programme, le transfert peut s’effectuer sans garanties 
                supplémentaires car ce prestataire est soumis à des obligations de protection 
                comparables à celles de la LPD.
              </li>
              <li>
                L’application de règles d’entreprise contraignantes (Binding Corporate Rules) lorsqu’il 
                s’agit de transferts au sein d’un même groupe d’entreprises multinational, ayant été 
                approuvées par les autorités compétentes. (Note : ceci est théorique dans notre cas, 
                n’étant pas actuellement une multinationale.)
              </li>
            </ul>
            <p>
              Dans tous les cas de transferts internationaux, nous évaluons au préalable le risque pour vos 
              droits et libertés et choisissons la solution la plus protectrice. Sur demande, nous sommes 
              disposés à vous fournir davantage d’informations sur les transferts de données vous 
              concernant (le cas échéant) et les garanties mises en place. Vous pouvez notamment nous 
              contacter pour savoir si vos données ont été transférées vers un pays tiers et obtenir une copie 
              des clauses de protection pertinentes (sous réserve de confidentialité).
            </p>
            <p>
              <strong>Pas de transfert non contrôlé :</strong> Hormis les cas prévus ci-dessus, nous n’effectuons aucun 
              transfert de vos données à l’étranger. En particulier, nous évitons d’utiliser des services cloud 
              non conformes ou localisés hors UE sans protection, afin de prévenir tout accès non autorisé 
              de juridictions étrangères à vos informations. Si, à l’avenir, un nouveau transfert devait être 
              envisagé, nous mettrions à jour cette politique en conséquence et, si nécessaire, recueillerions 
              votre consentement lorsque la loi l’exige.
            </p>
          </div>
        </section>

        <section id="data-retention" className="scroll-mt-24">
          <h3 className="text-lg font-semibold mb-4">6. Durées de conservation</h3>
          <div className="space-y-4">
            <p>
              Nous conservons vos données personnelles pendant une durée n’excédant pas celle nécessaire 
              au regard des finalités pour lesquelles elles ont été collectées (telles que décrites en section 3), 
              sous réserve des obligations légales de conservation plus longues. Voici les principales 
              catégories de données et les durées de conservation associées :
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong>Données de votre compte et profil utilisateur :</strong> Vos informations de compte (par ex. 
                nom, email, informations du profil, CV et documents que vous avez importés) sont 
                conservées tant que votre compte reste actif. Vous avez la possibilité de modifier ou 
                supprimer à tout moment les données de votre profil via votre espace personnel. Si 
                vous décidez de supprimer votre compte, soit via une fonctionnalité en ligne soit en 
                nous contactant, toutes les données associées à votre compte seront effacées ou 
                anonymisées dans un délai bref (généralement sous 30 jours) après la réception de 
                votre demande, sauf celles que nous devons impérativement conserver plus longtemps 
                en vertu de la loi (voir ci-dessous). De même, si votre compte demeure inactif pendant 
                une longue période, nous pourrons vous contacter pour vous informer de la 
                désactivation ou suppression envisagée de votre compte pour inactivité. En l’absence 
                de réponse, et sauf obligation contraire, un compte inactif depuis 24 mois ou plus 
                pourrait être supprimé et les données effacées, afin de limiter la conservation indéfinie 
                de données inutilisées. Bien entendu, nous conserverons les données d’un compte 
                inactif plus longtemps si vous avez souscrit un service payant qui reste valable, ou s’il 
                existe un litige ou une obligation légale l’exigeant.
              </li>
              <li>
                <strong>Contenu du CV, lettres de motivation et résultats d’analyse :</strong> Les documents que vous 
                importez (par ex. votre CV, les offres d’emploi ciblées) ainsi que les lettres de 
                motivation générées et les analyses effectuées sont traités comme faisant partie 
                intégrante de votre compte. Ils sont donc conservés aussi longtemps que votre compte 
                est actif, sauf si vous les supprimez vous-même plus tôt. Vous pouvez à tout moment 
                supprimer ou remplacer un CV ou toute lettre enregistrée dans votre espace. Si vous 
                supprimez votre compte, ces contenus seront effacés dans les mêmes délais que le 
                reste du compte. Il est possible que nous conservions de manière anonymisée certaines 
                données dérivées (par ex. statistiques globales tirées de plusieurs CV, modèles 
                améliorés grâce à des données agrégées) au-delà de la suppression du compte, mais 
                ces données ne permettront plus de remonter jusqu’à vous.
              </li>
              <li>
                <strong>Données de contact et support :</strong> Les communications que vous échangez avec nous 
                (demandes de support, emails envoyés à support@cvmatchswiss.ch, chats éventuels) 
                sont conservées le temps de traiter votre demande puis archivées pour un suivi interne. 
                En règle générale, nous conservons ces correspondances pendant 2 ans après la clôture 
                de votre demande, afin de garder une trace en cas de reprise de contact pour le même 
                sujet ou de besoin de référence. Dans certains cas particuliers (par ex. une réclamation 
                importante, un litige, ou une demande d’exercice de droits LPD/RGPD), nous 
                pourrons conserver les échanges plus longtemps, jusqu’à 5 ans voire au-delà si c’est 
                nécessaire pour établir, exercer ou défendre nos droits en justice. Passé ces délais, les 
                communications sont soit supprimées, soit anonymisées si nous souhaitons en 
                conserver la substance (par ex. pour constituer une FAQ anonymisée des questions 
                fréquentes).
              </li>
              <li>
                <strong>Données de paiement et facturation :</strong> Les enregistrements relatifs aux transactions 
                financières (par ex. preuves de paiement, factures émises, historique des abonnements) 
                sont conservés pendant la durée exigée par la loi fiscale et commerciale suisse. 
                Conformément aux obligations du Code des obligations (CO) en matière de pièces 
                comptables, nous gardons ces données 10 ans à compter de la fin de l’exercice 
                comptable concernéppdt-june.ch. Cela inclut vos coordonnées de facturation et détails 
                de paiement (à l’exclusion, rappelons-le, des données sensibles de carte bancaire que 
                nous ne détenons pas), ainsi que les factures/remboursements. Ces données sont 
                archivées de manière sécurisée et l’accès en est restreint au service comptable ou aux 
                autorités compétentes en cas de contrôle. Une fois le délai légal expiré, ces 
                informations sont supprimées ou dûment archivées (si obligation patrimoniale) de 
                façon à ne plus être facilement accessibles.
              </li>
              <li>
                <strong>Données techniques et journaux :</strong> Les journaux de connexion et d’activité (logs) que 
                nous collectons à des fins de sécurité et de performance sont conservés pour une durée 
                limitée, proportionnée à l’objectif. Typiquement, les logs d’activité applicative et les 
                journaux de serveur web sont conservés pendant 12 mois maximum. Les journaux plus 
                sensibles de sécurité (par ex. trace des accès, modifications de données importantes) 
                peuvent être conservés jusqu’à 24 mois afin de pouvoir enquêter sur d’éventuels 
                incidents passés. Au-delà de ces durées, ils sont automatiquement supprimés ou 
                anonymisés, sauf si un incident majeur en cours nécessite de les garder plus longtemps 
                (par ex. logs bloqués le temps d’une enquête de sécurité). Les backups (sauvegardes) 
                de nos bases de données, quant à eux, sont généralement chiffrés et conservés en 
                rotation sur une période glissante de 30 à 90 jours, puis écrasés par les backups plus 
                récents. Ainsi, même après la suppression de vos données en base active, il peut 
                subsister des traces dans nos backups durant ce délai, mais celles-ci sont protégées et 
                ne sont utilisées qu’en cas de restauration du système (jamais pour réintroduire des 
                données supprimées à la demande d’un utilisateur). Passé le délai de rétention des 
                backups, les données supprimées disparaissent définitivement.
              </li>
              <li>
                <strong>Cookies et données de navigation :</strong> Les cookies utilisés sur notre site ont chacun une 
                durée de vie définie (voir section 10 pour plus de détails sur chaque cookie). De 
                manière générale, les cookies de session (nécessaires au fonctionnement du site, par 
                ex. pour vous maintenir connecté) expirent dès que vous fermez votre navigateur ou se 
                déconnectent après un court délai d’inactivité. Les cookies de préférence ou d’analyse 
                anonymisée peuvent avoir une durée de vie allant de quelques jours à quelques mois 
                (par ex. un cookie de préférence de langue peut être conservé 6 mois, un cookie 
                d’analyse du trafic 13 mois maximum conformément aux recommandations 
                européennes). Dans tous les cas, nous ne conservons pas les informations issues de 
                cookies plus longtemps que la durée de vie du cookie lui-même ; au-delà, les données 
                collectées sont soit supprimées, soit agrégées.
              </li>
            </ul>
            <p>
              Au terme des durées de conservation indiquées ci-dessus, nous procédons soit à l’effacement 
              sécurisé des données personnelles concernées, soit à leur anonymisation irréversible (de sorte 
              qu’elles ne constituent plus des données personnelles au sens de la loi). Par exemple, nous 
              pourrons garder des statistiques globales d’utilisation du service ou des tendances de 
              recrutement issues des CV de nos utilisateurs, mais sans aucune information identifiable.
            </p>
            <p>
              <strong>Exceptions :</strong> Il se peut que, pour répondre à des obligations légales particulières (par ex. 
              gestion d’un contentieux, réquisition légale) ou pour protéger nos droits en justice, nous 
              devions conserver certaines données au-delà des durées standard. Dans ce cas, nous isolerons 
              ces données dans un stockage d’archive sécurisé avec un accès restreint, et nous les 
              supprimerons dès que la raison justifiant leur conservation prolongée aura expiré.
            </p>
          </div>
        </section>

        <section id="user-rights" className="scroll-mt-24">
          <h3 className="text-lg font-semibold mb-4">7. Droits des utilisateurs</h3>
          <div className="space-y-6">
            <p>
              En tant qu’utilisateur et personne concernée par un traitement de données personnelles, vous 
              disposez d’un ensemble de droits légaux visant à vous donner le contrôle sur vos 
              informations. Ces droits, issus pour l’essentiel du RGPD européen et reconnus pour la plupart 
              par la LPD suisse, sont les suivants :
            </p>
            <div>
              <h4 className="font-semibold mb-2">Droit d’accès</h4>
              <p>
                Vous avez le droit de nous demander si nous détenons des données 
                personnelles vous concernant et d’en obtenir une copie intelligible, ainsi que des 
                informations sur les finalités du traitement, les catégories de données concernées, les 
                destinataires à qui les données ont été communiquées, la durée de conservation 
                prévue, etc. Sur simple demande de votre part, nous vous communiquerons donc les 
                données vous concernant que nous traitons, sauf restrictions légales particulières.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-2">Droit de rectification</h4>
              <p>
                Si vous constatez que des données personnelles vous 
                concernant sont inexactes ou incomplètes, vous pouvez exiger qu’elles soient 
                corrigées ou complétées. Par exemple, si votre nom ou votre adresse a changé, ou si 
                une information de votre profil est erronée, nous procéderons à la mise à jour sans 
                retard injustifié. La majorité de vos données de compte (profil, CV, etc.) pouvant être 
                modifiées directement via votre espace CVMatch, la rectification peut souvent être 
                effectuée par vous-même en temps réel. Pour toute autre donnée non modifiable par 
                vous, il suffit de nous contacter et nous ferons le nécessaire.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-2">Droit à l’effacement (« droit à l’oubli »)</h4>
              <p>
                Vous pouvez demander la suppression de 
                vos données personnelles dans nos systèmes, dans la mesure permise par la loi. Ce 
                droit s’applique par exemple si vous retirez votre consentement (pour les traitements 
                fondés sur le consentement), si vous vous opposez à un traitement et que nous n’avons 
                pas de motif légitime supérieur pour le continuer, ou si vos données ne sont plus 
                nécessaires au regard des finalités initiales. Il s’applique également si nous traitions 
                vos données de manière illicite ou pour se conformer à une obligation légale 
                d’effacement. Concrètement, la suppression de votre compte (et des données 
                afférentes) entre dans ce cadre : vous pouvez à tout moment supprimer votre compte 
                via les paramètres du site/app ou en nous envoyant une demande explicite. Nous 
                attirons votre attention sur le fait que certains droits légaux peuvent nous obliger à 
                conserver certaines données malgré votre demande d’effacement – par ex. nous ne 
                pourrons pas effacer immédiatement des données de transaction encore requises pour 
                nos obligations comptables, mais celles-ci seront bloquées et isolées jusqu’à la fin de 
                la période légale. En tout état de cause, si nous devons refuser un effacement total 
                d’une donnée, nous vous en expliquerons la raison juridique. En l’absence de 
                contrainte particulière, nous accédons à toute demande d’effacement dans les 
                meilleurs délais.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-2">Droit d’opposition</h4>
              <p>
                Vous avez le droit de vous opposer à tout moment pour des 
                raisons tenant à votre situation particulière à un traitement de vos données 
                personnelles fondé sur notre intérêt légitime (voir section 2). Si vous exercez ce droit, 
                nous cesserons le traitement en question sauf si nous pouvons démontrer qu’il existe 
                des motifs légitimes impérieux pour le poursuivre (par ex. nécessité vitale, intérêt 
                public important) ou si le traitement est nécessaire à la constatation, l’exercice ou la 
                défense de droits en justice. S’agissant de la prospection commerciale, vous avez en 
                tout état de cause le droit de vous opposer sans motif à tout moment à l’utilisation de 
                vos données à de telles fins (par ex. si nous vous envoyons des e-mails d’information 
                ou newsletters, vous pouvez vous y opposer et nous cesserons immédiatement). Des 
                liens de désinscription figureront d’ailleurs en pied de page de tout e-mail non 
                essentiel que nous pourrions vous envoyer.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-2">Droit à la limitation du traitement</h4>
              <p>
                Ce droit vous permet de nous demander de 
                « geler » temporairement l’utilisation de certaines de vos données dans certaines 
                situations. Par exemple, si vous contestez l’exactitude de données vous concernant, 
                vous pouvez demander la limitation du traitement pendant le temps que nous vérifions 
                l’exactitude des données (nous ne pourrons alors plus que stocker la donnée, mais ni 
                l’utiliser ni la modifier). De même, si vous vous êtes opposé à un traitement, vous 
                pouvez exiger une limitation pendant la période de vérification de la balance des 
                intérêts. Ce droit peut aussi s’exercer si un traitement est illicite mais que vous ne 
                souhaitez pas un effacement total (par ex. vous voulez que nous conservions la donnée 
                mais sans l’exploiter), ou encore si vous avez besoin de nous laisser la donnée pour la 
                constatation, l’exercice ou la défense de droits en justice. En cas de limitation, le 
                traitement reprendra soit avec votre consentement, soit si nécessaire pour des raisons 
                légales, et vous en serez informé au préalable.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-2">Droit à la portabilité des données</h4>
              <p>
                Pour les données personnelles que vous nous avez 
                fournies directement et que nous traitons par des moyens automatisés sur la base de 
                votre consentement ou dans le cadre d’un contrat, vous bénéficiez du droit de 
                récupérer ces données dans un format structuré, couramment utilisé et lisible par 
                machine, afin de pouvoir éventuellement les transmettre à un autre prestataire. 
                Concrètement, cela signifie par exemple que vous pouvez nous demander une 
                exportation de vos données de profil et de vos CV/lettres sous un format électronique 
                standard (JSON, CSV, etc.) pour les réutiliser ailleurs. Lorsque cela est techniquement 
                possible, vous pouvez même nous demander de transmettre ces données directement à 
                un tiers que vous désignez (par ex. si vous voulez importer votre profil sur une autre 
                plateforme). Notez que ce droit à la portabilité ne porte que sur les données « brutes » 
                que vous avez fournies (et les données dérivées que nous avons générées avec votre 
                accord ou pour l’exécution du service). Il n’inclut pas les données déjà anonymisées ni 
                les analyses internes que nous avons produites en interne.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-2">Droit de retirer votre consentement</h4>
              <p>
                Dans les cas où le traitement de vos données est 
                fondé sur votre consentement (voir section 2), vous pouvez retirer ce consentement à 
                tout moment. Ce retrait n’aura pas d’effet rétroactif, mais il vaudra pour l’avenir. Par 
                exemple, si vous aviez consenti à recevoir notre newsletter, vous pouvez vous 
                désinscrire et nous ne vous enverrons plus de communications marketing. Si vous 
                aviez autorisé l’utilisation de certaines données facultatives, nous cesserons de les 
                traiter une fois le consentement retiré. Le retrait du consentement n’affecte pas la 
                licéité du traitement effectué avant ce retrait.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-2">Droit de déposer une réclamation</h4>
              <p>
                Si vous estimez que vos données ont été traitées de 
                manière non conforme à la loi ou que vos droits n’ont pas été respectés, vous avez le 
                droit d’introduire une réclamation auprès de l’autorité de contrôle compétente en 
                matière de protection des données. Pour les utilisateurs en Suisse, il s’agit du Préposé 
                fédéral à la protection des données et à la transparence (PFPDT). Pour les utilisateurs 
                situés dans l’UE, vous pouvez contacter l’autorité de protection des données de votre 
                pays de résidence (par ex. la CNIL en France, le CPVP en Belgique, le BDI en 
                Allemagne, etc.). Nous vous encourageons toutefois, avant de saisir une autorité, à 
                nous contacter directement afin que nous puissions répondre à vos préoccupations – la 
                satisfaction de nos utilisateurs et le respect de nos obligations légales sont au cœur de 
                nos priorités, et nous ferons tout notre possible pour résoudre en amont tout problème 
                éventuel.
              </p>
            </div>
            <p>
              <strong>Exercice de vos droits :</strong> Vous pouvez exercer les droits listés ci-dessus à tout moment en nous 
              contactant via les coordonnées fournies à la section 12 (par e-mail de préférence, ou par 
              courrier). Afin de protéger votre confidentialité, nous pourrions vous demander de nous 
              fournir des informations supplémentaires pour vérifier votre identité si nous avons un doute 
              sur la personne à l’origine de la demande (par ex. nous pourrions solliciter une confirmation 
              via l’adresse e-mail enregistrée dans votre compte, ou un justificatif d’identité pour les 
              demandes sensibles). Nous vous répondrons dans les meilleurs délais, et en tout état de cause 
              dans un délai d’un mois à compter de la réception de votre demande (délai pouvant être 
              prolongé de deux mois supplémentaires pour les demandes complexes ou multiples, 
              conformément au RGPD – le cas échéant, nous vous en informerons). L’exercice de vos droits 
              est gratuit. Toutefois, des demandes manifestement infondées ou excessives (par ex. 
              répétitives sans raison) pourront être refusées ou faire l’objet d’un léger coût de traitement 
              conformément à la réglementation.
            </p>
            <p>
              <strong>Absence de décision automatisée individuelle :</strong> Il est important de noter que vous ne faites 
              l’objet d’aucune décision fondée exclusively sur un traitement automatisé produisant des 
              effets juridiques vous concernant ou vous affectant de manière significative, au sens de 
              l’article 22 du RGPD. En d’autres termes, les analyses et recommandations fournies par 
              CVMatchSwiss (par ex. score de correspondance CV - offre, lettre de motivation générée) sont 
              des aides à la décision que vous êtes libre d’utiliser ou non, mais elles ne constituent pas en 
              soi des décisions vous concernant. CVMatchSwiss n’effectue pas de profilage automatisé 
              dans le but d’évaluer certains aspects personnels vous concernant en dehors du périmètre de 
              service que vous avez initié. Si à l’avenir nous devions mettre en place un dispositif de 
              décision automatisée susceptible de vous affecter de manière significative, nous nous 
              conformerions aux exigences légales en la matière (information préalable spécifique, 
              possibilité d’intervention humaine, etc.).
            </p>
            <p>
              Nous nous engageons à respecter pleinement vos droits et à vous fournir toute assistance 
              nécessaire pour les exercer. La transparence et le contrôle par l’utilisateur sont des principes 
              fondamentaux. N’hésitez donc pas à nous solliciter pour toute question ou demande relative à 
              vos données personnelles.
            </p>
          </div>
        </section>

        <section id="mobile-app" className="scroll-mt-24">
          <h3 className="text-lg font-semibold mb-4">8. Spécificités App Store / Google Play</h3>
          <div className="space-y-4">
            <p>
              En plus de notre site web, CVMatchSwiss est accessible via une application mobile 
              téléchargeable sur l’Apple App Store (iOS) et le Google Play Store (Android). L’utilisation de 
              notre application mobile implique quelques considérations supplémentaires en matière de 
              protection des données :
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong>Distribution de l’application :</strong> Lorsque vous téléchargez l’application CVMatchSwiss 
                depuis l’App Store ou Google Play, sachez que vous utilisez les plateformes de 
                distribution d’Apple Inc. et de Google LLC, qui ont leurs propres politiques de 
                confidentialité et conditions d’utilisation. Ni Apple ni Google ne sont nos sous-
                traitants directs pour le traitement de vos données au sein de l’application, mais ils 
                peuvent collecter certaines informations pour leur propre compte lors du processus de 
                téléchargement ou d’utilisation générale de votre appareil (par ex. statistiques 
                d’utilisation anonymes, identification de votre appareil, interactions nécessaires pour 
                la livraison des mises à jour, etc.). Nous vous invitons à consulter les politiques de 
                confidentialité d’Apple et de Google pour comprendre les données qu’ils peuvent 
                collecter de leur côté lorsque vous utilisez leurs boutiques et environnements. 
                CVMatchSwiss ne partage pas proactivement de données personnelles vous 
                concernant avec Apple ou Google, à l’exception des informations indispensables au 
                fonctionnement technique de l’application sur leurs systèmes (par ex. lors de la 
                vérification de licence de l’appli, ou l’envoi de notifications push via leurs serveurs – 
                voir ci-dessous).
              </li>
              <li>
                <strong>Notifications push :</strong> Notre application mobile peut, avec votre accord, vous envoyer 
                des notifications push (par ex. pour vous avertir de la fin d'une analyse de CV, de la 
                disponibilité d'une nouvelle fonctionnalité, ou d'une alerte que vous avez paramétrée). 
                Sur iOS comme sur Android, l'envoi de ces notifications repose sur les services des 
                plateformes (Apple Push Notification service pour iOS) et sur notre infrastructure 
                AWS pour Android. Pour ce faire, lors de l'installation, l'application vous demande 
                l'autorisation d'envoyer des notifications. Si vous l'acceptez, un identifiant de votre 
                appareil (jeton de notification) sera généré et utilisé par nos serveurs pour adresser les 
                messages via Apple ou notre service de notifications hébergé sur AWS. Cet identifiant 
                ne contient pas en lui-même de données personnelles identifiantes, et il est utilisé 
                uniquement pour le routage technique des notifications. Vous pouvez à tout moment 
                désactiver les notifications dans les réglages de l'application ou de votre appareil si 
                vous ne souhaitez plus en recevoir.
              </li>
              <li>
                <strong>Autorisations de l’application :</strong> En dehors des notifications, l’application peut vous 
                demander certaines autorisations pour accéder à des fonctionnalités de votre appareil, 
                si cela est nécessaire au service. Par exemple, si nous offrons la possibilité d’importer 
                un CV depuis votre stockage local, l’application pourrait demander l’accès à vos 
                fichiers (stockage) – exclusivement pour vous permettre de sélectionner le fichier 
                voulu. De même, si une fonction de prise de photo pour votre profil ou de scan de 
                document était disponible, l’appli solliciterait l’accès à l’appareil photo, mais 
                uniquement suite à votre action explicite. Dans tous les cas, ces autorisations sont 
                optionnelles : nous ne les sollicitons que lorsque vous utilisez la fonction 
                correspondante, et vous êtes libre de les refuser. Un refus peut simplement vous 
                empêcher d’utiliser la fonction en question (par ex. sans accès fichiers, vous ne 
                pourrez pas charger un CV depuis le téléphone, mais vous pourrez toujours le faire via 
                un autre moyen). Vous pouvez gérer et révoquer à tout moment ces autorisations dans 
                les réglages de votre smartphone.
              </li>
              <li>
                <strong>Collecte de données analytiques mobiles :</strong> Pour améliorer notre application mobile, 
                nous pourrons collecter des données d’erreur ou de performance spécifiques à 
                l’application. Par exemple, en cas de crash de l’appli, un rapport d’erreur anonymisé 
                peut être envoyé (avec votre consentement implicite via les réglages de votre appareil) 
                contenant des informations techniques (modèle d’appareil, version de l’appli, journal 
                d’erreur). Ces rapports nous aident à corriger les bugs et ne contiennent pas de 
                données personnelles telles que le contenu de votre CV ou votre identité. De même, 
                nous pouvons utiliser un outil d’analyse intégré pour comprendre quelles 
                fonctionnalités de l’appli sont les plus utilisées, dans le but d’améliorer l’ergonomie. 
                Ces analyses se font de manière agrégée et ne suivent pas votre activité à travers 
                d’autres applications.
              </li>
              <li>
                <strong>Achats In-App :</strong> Si l’application propose des achats intégrés (par ex. souscription à un 
                plan Premium via l’App Store/Play Store), ces transactions sont gérées par Apple ou 
                Google eux-mêmes via votre compte App Store/Google Play. Dans ce cas, c’est 
                Apple/Google qui collecte les informations de paiement via votre compte 
                App Store/Google Play, et qui nous reverse ensuite les montants dus. Nous ne 
                recevons pas vos coordonnées bancaires lors d’un achat via les stores, seulement une 
                confirmation de la part d’Apple/Google que l’achat a bien eu lieu, afin de débloquer le 
                service correspondant dans l’application. Les règles de facturation et de 
                remboursement de l’App Store/Google Play s’appliquent. Pensez à consulter les 
                conditions d’achats des plateformes pour plus de détails.
              </li>
            </ul>
            <p>
              <strong>Liens vers les politiques des plateformes :</strong> Pour votre information, voici où trouver les 
              politiques de confidentialité des deux plateformes :
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Apple (App Store) : Politique de confidentialité d’Apple disponible sur apple.com/legal/privacy.</li>
              <li>Google (Google Play) : Règles de confidentialité de Google disponibles sur policies.google.com/privacy.</li>
            </ul>
            <p>
              En résumé, l’utilisation de via l’application mobile n’entraîne pas de partage supplémentaire 
              de vos données personnelles avec Apple ou Google en dehors des mécanismes standard 
              nécessaires (notifications, achats intégrés). Nous appliquons les mêmes principes de 
              minimisation et de protection des données sur l’application que sur le site web. Si vous avez 
              des questions spécifiques à la version mobile de nos services, n’hésitez pas à nous contacter.
            </p>
          </div>
        </section>

        <section id="data-security" className="scroll-mt-24">
          <h3 className="text-lg font-semibold mb-4">9. Sécurité des données</h3>
          <div className="space-y-4">
            <p>
              Nous mettons en œuvre des mesures de sécurité techniques et organisationnelles robustes afin 
              de protéger vos données personnelles contre tout accès non autorisé, toute perte, destruction, 
              divulgation ou altération. La sécurité de vos informations est une priorité. Voici un aperçu de 
              nos pratiques en la matière :
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong>Chiffrement :</strong> Toutes les communications entre votre appareil (navigateur web ou 
                application mobile) et nos serveurs sont chiffrées via le protocole HTTPS (TLS). Cela 
                signifie que les données que vous transmettez (par ex. lorsque vous téléchargez votre 
                CV ou consultez vos résultats) sont cryptées durant le transit, empêchant les tiers d’en 
                intercepter le contenu. De plus, nous chiffrons les données sensibles au repos lorsque 
                c’est approprié – par exemple, les mots de passe de compte sont stockés sous forme 
                hachée (et salée), selon les standards de l’industrie, ce qui les rend illisibles même en 
                cas d’intrusion.
              </li>
              <li>
                <strong>Contrôle d’accès :</strong> En interne, l’accès aux données personnelles est strictement limité 
                aux collaborateurs et sous-traitants qui en ont besoin pour exercer leurs fonctions 
                (principe du « besoin d’en connaître »). Par exemple, l’équipe technique peut accéder 
                aux données de base nécessaires au support et à l’entretien du système, mais n’a pas 
                de raison d’accéder au contenu détaillé de vos CV ou lettres en dehors d’une demande 
                expresse de support de votre part. Chaque membre de l’équipe est soumis à une 
                obligation contractuelle de confidentialité et formé aux bonnes pratiques de protection 
                des données. Nous utilisons des systèmes d’authentification sécurisés, des journaux 
                d’accès et des restrictions de droits pour nous assurer qu’aucun accès non autorisé n’a 
                lieu en interne.
              </li>
              <li>
                <strong>Infrastructure sécurisée :</strong> Nos serveurs et bases de données sont protégés par des pare-
                feu, des systèmes de détection/prévention d’intrusion, et hébergés dans des centres de 
                données à la pointe de la sécurité physique et logicielle. Les centres d’hébergement 
                (ex. AWS) certifient respecter des normes de sécurité internationales (ISO 27001, SOC 
                2, etc.). Les données sont sauvegardées régulièrement (backups chiffrés) pour prévenir 
                toute perte accidentelle, et ces backups sont stockés dans des emplacements sécurisés 
                distincts.
              </li>
              <li>
                <strong>Tests et maintenance :</strong> Nous maintenons nos logiciels et dépendances à jour afin de 
                corriger les vulnérabilités connues (politique de patch management active). Notre 
                application fait l’objet de tests réguliers, y compris des tests internes de type 
                penetration testing ou analyses automatisées de vulnérabilités. Si des failles de 
                sécurité sont découvertes, nous les corrigeons en priorité. De plus, nous suivons de 
                près les bonnes pratiques et recommandations de la communauté en matière de 
                sécurité applicative (OWASP Top 10, etc.).
              </li>
              <li>
                <strong>Plan de réponse aux incidents :</strong> Bien que nous fassions tout pour éviter les incidents, 
                aucun système n’est totalement infaillible. C’est pourquoi nous avons mis en place un 
                plan de réponse aux incidents de sécurité. En cas de suspicion de violation de données 
                personnelles (par ex. accès illicite, fuite de données), nous nous engageons à : (1) 
                prendre immédiatement les mesures nécessaires pour contenir l’incident et le 
                neutraliser, (2) évaluer l’étendue de la violation et la nature des données affectées, (3) 
                si requis par la loi, notifier l’événement à l’autorité de protection des données 
                compétente (PFPDT en Suisse, et/ou autorités européennes via un éventuel 
                représentant, dans les délais légaux, généralement 72h) ainsi qu’aux utilisateurs 
                concernés si la violation est susceptible d’engendrer un risque élevé pour leurs droits 
                et libertés. Nous vous tiendrions informé des actions entreprises et vous conseillerions 
                sur les mesures de protection additionnelles éventuellement nécessaires.
              </li>
              <li>
                <strong>Confidentialité dès la conception :</strong> Nous appliquons le principe de « Privacy by Design 
                & by Default » lors du développement de nos fonctionnalités. Concrètement, cela 
                signifie que la protection des données est prise en compte dès la phase de conception 
                de nos services (minimisation des données collectées, paramétrages par défaut 
                respectueux de la vie privée, etc.) et tout au long du cycle de vie du produit. Par 
                exemple, les informations de profil que vous fournissez ne sont visibles que par vous, 
                par défaut, et non par d’autres utilisateurs ; l’architecture du système cloisonne les 
                données de chaque utilisateur de sorte qu’aucun utilisateur ne puisse accéder aux 
                données d’un autre.
              </li>
              <li>
                <strong>Sensibilisation :</strong> L’ensemble de l’équipe est sensibilisé et formé aux enjeux de la 
                sécurité et de la confidentialité. Nous veillons à instaurer une culture interne où 
                chaque employé ou partenaire comprend l’importance de protéger les données des 
                utilisateurs et connaît les procédures à suivre pour éviter les erreurs ou les 
                comportements à risque.
              </li>
            </ul>
            <p>
              En dépit de tous nos efforts, il est également important que vous preniez certaines précautions 
              de votre côté pour contribuer à la sécurité de vos données. Choisissez un mot de passe robuste 
              pour votre compte et gardez-le confidentiel. Ne le partagez pas avec des tiers, et évitez de 
              réutiliser le même mot de passe que pour d’autres services. Si vous soupçonnez qu’une 
              personne non autorisée a accédé à votre compte, changez immédiatement votre mot de passe 
              et avertissez-nous. De notre côté, nous ne vous demanderons jamais votre mot de passe par email 
              ou par tout autre moyen non sécurisé.
            </p>
            <p>
              En résumé, nous investissons de manière continue dans la sécurité de notre plateforme afin de 
              mériter votre confiance. La protection de vos données n’est pas seulement une obligation 
              légale pour nous, c’est un engagement moral vis-à-vis de vous en tant qu’utilisateur.
            </p>
          </div>
        </section>

        <section id="cookies" className="scroll-mt-24">
          <h3 className="text-lg font-semibold mb-4">10. Cookies</h3>
          <div className="space-y-4">
            <p>
              Les cookies sont de petits fichiers texte enregistrés sur votre terminal (ordinateur, smartphone, 
              etc.) lors de la consultation de sites web, et qui permettent de stocker et récupérer des 
              informations sur votre navigation. CVMatchSwiss utilise des cookies et technologies 
              similaires de manière raisonnée et dans le respect des réglementations applicables (directive 
              “ePrivacy” de l’UE et règles équivalentes en Suisse). Nous privilégions un usage minimal des 
              cookies, de sorte que dans la mesure du possible, nos cookies soient exemptés de 
              consentement (car strictement nécessaires ou purement fonctionnels/statistiques sans atteinte à 
              la vie privée).
            </p>
            <h4 className="font-semibold mb-2">Cookies utilisés :</h4>
            <p>Nous utilisons les catégories de cookies suivantes :</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong>Cookies strictly nécessaires :</strong> Ces cookies sont indispensables au fonctionnement 
                du site et de l’application. Sans eux, certaines fonctionnalités ne pourraient pas être 
                fournies correctement. Par exemple, nous utilisons un cookie de session pour garder 
                votre connexion ouverte lorsque vous naviguez d’une page à l’autre après vous être 
                identifié. Ce cookie de session (généralement nommé de façon aléatoire, ex: sessionid) 
                contient un identifiant unique de session, mais pas de données personnelles en clair. Il 
                est stocké dans votre navigateur et se supprime automatiquement lorsque vous vous 
                déconnectez ou après un certain délai d’inactivité. D’autres cookies nécessaires 
                peuvent inclure vos préférences d’interface (langue choisie, préférences d’affichage), 
                afin d’améliorer votre expérience utilisateur. Par exemple, un cookie peut retenir que 
                vous préférez la version française du site, pour vous rediriger directement vers celle-ci 
                lors de votre prochaine visite. Ces cookies nécessaires ne requièrent pas votre 
                consentement préalable, car sans eux le service ne pourrait pas fonctionner de manière 
                sécurisée et efficace. Vous ne pouvez pas les désactiver depuis notre panneau car ils 
                sont essentiels, mais vous pouvez toujours les bloquer via votre navigateur (au risque 
                d’altérer les fonctionnalités du site).
              </li>
              <li>
                <strong>Cookies de mesure d’audience (statistiques) :</strong> Afin de comprendre comment notre site 
                est utilisé et améliorer sa performance, nous utilisons des cookies analytiques. Nous le 
                faisons de manière respectueuse de la vie privée, c’est-à-dire en privilégiant des outils 
                internes ou paramétrés pour anonymiser les données collectées. Par exemple, si nous 
                utilisons un cookie de Google Analytics ou d’un outil équivalent, nous activons 
                l’anonymisation des adresses IP et n’utilisons pas de fonctionnalités publicitaires. Ces 
                cookies recueillent des informations telles que les pages visitées, le temps passé sur le 
                site, les interactions avec telle ou telle fonctionnalité, le chemin de navigation, etc., 
                mais sans vous identifier personnellement. Les données obtenues nous servent 
                uniquement à établir des statistiques globales (par ex. nombre de visiteurs par jour, 
                taux d’utilisation d’une fonctionnalité) et à détecter d’éventuels problèmes de 
                navigation. Lorsque cela est possible, nous paramétrons ces cookies de mesure 
                d’audience de sorte qu’ils soient dispensés de consentement (par ex. en respectant les 
                lignes directrices des autorités – pas de recoupement avec d’autres sources, durée de 
                vie limitée, anonymisation immédiate de l’IP, etc.). Actuellement, notre analyse 
                d’audience n’implique aucun transfert de données personnelles à des tiers : soit elle est 
                effectuée en interne, soit via un prestataire européen respectueux (voir section 4). La 
                durée de vie de ces cookies est généralement de quelques mois (par ex. un cookie 
                _analytics expirant au bout de 6 à 12 mois). Vous avez néanmoins la possibilité de les 
                refuser ou de les supprimer à tout moment (voir plus bas).
              </li>
              <li>
                <strong>Cookies fonctionnels optionnels :</strong> Il se peut que nous utilisions ponctuellement 
                d’autres cookies pour des fonctionnalités spécifiques, mais uniquement avec votre 
                consentement. Par exemple, si nous intégrons une vidéo explicative de nos services 
                via YouTube ou Vimeo, ces plateformes pourraient déposer leurs propres cookies 
                lorsque vous lancez la vidéo – dans ce cas, nous vous en avertirions et vous auriez le 
                choix de lancer ou non ce contenu externe. À ce jour, nous évitons d’inclure des 
                modules tiers intrusifs. Si à l’avenir nous introduisons un cookie visant à personnaliser 
                davantage votre expérience ou à tester de nouvelles fonctionnalités (par ex. A/B 
                testing), nous veillerons à ce qu’il respecte la législation et nous mettrons à jour la 
                présente section en conséquence.
              </li>
            </ul>
            <h4 className="font-semibold mb-2 mt-4">Gestion des cookies :</h4>
            <p>
              Lors de votre première visite sur notre site, un bandeau d’information 
              succinct peut vous informer de l’utilisation de cookies indispensables au fonctionnement du 
              service et, le cas échéant, vous proposer d’accepter ou paramétrer les cookies non essentiels. 
              Étant donné notre politique d’utilisation minimale, nous cherchons à nous passer de tout 
              cookie nécessitant un consentement explicite. Toutefois, si nous devions en utiliser (par ex. un 
              cookie d’analyse non exempté), nous recueillerions votre consentement via ce bandeau de 
              manière claire et vous pourriez le refuser sans conséquence sur les fonctions principales.
            </p>
            <p>
              En tout état de cause, vous gardez le contrôle sur les cookies. Vous pouvez à tout moment 
              paramétrer votre navigateur pour qu’il refuse les cookies ou vous alerte lors de leur dépôt. 
              Vous pouvez également supprimer les cookies déjà stockés via les fonctionnalités de votre 
              navigateur (généralement dans les options de confidentialité ou historique). Veuillez noter que 
              la désactivation des cookies strictement nécessaires peut compromettre certaines 
              fonctionnalités (par ex. vous pourriez ne plus pouvoir vous connecter ou votre session 
              pourrait ne pas persister). En revanche, le refus des cookies analytiques ou optionnels 
              n’affectera pas le cœur du service.
            </p>
            <p>Pour gérer vos cookies dans les principaux navigateurs :</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Chrome : menu Paramètres &gt; Confidentialité et sécurité &gt; Cookies et autres données de site.</li>
              <li>Firefox : Options &gt; Vie privée et sécurité &gt; Cookies et données de sites.</li>
              <li>Safari : Préférences &gt; Confidentialité &gt; Gérer les données du site web.</li>
              <li>Edge : Paramètres &gt; Cookies et autorisations de site.</li>
            </ul>
            <p>
              Si vous utilisez notre application mobile, il n’y a pas de cookies à proprement parler, mais des 
              technologies équivalentes peuvent exister (stockage local, SDK d’analyse). Ces dernières 
              suivent les mêmes principes : nous les utilisons à minima et vous pouvez contrôler certaines 
              autorisations via les réglages de l’app (voir section 8).
            </p>
            <p>
              Nous maintenons à jour la liste des cookies et technologies similaires utilisées dans un Avis 
              relatif aux cookies détaillé (accessible éventuellement via un lien en pied de page du site). 
              Vous y trouverez la liste de chaque cookie, sa finalité, sa durée de vie et la façon de s’y 
              opposer. N’hésitez pas à le consulter pour une information complète et à jour.
            </p>
          </div>
        </section>

        <section id="minors" className="scroll-mt-24">
          <h3 className="text-lg font-semibold mb-4">11. Mineurs</h3>
          <div className="space-y-4">
            <p>
              Les services s’adressent à un public adulte ou du moins majeur au sens des lois sur la 
              protection des données. En particulier, notre plateforme n’est pas destinée aux enfants de 
              moins de 16 ans. Si vous avez moins de 16 ans, nous vous prions de ne pas utiliser 
              CVMatchSwiss et de ne pas nous fournir de données personnelles. Si malgré cela un mineur 
              de moins de 16 ans s’inscrit en contournant nos contrôles ou utilise nos services, nous 
              considérerons que c’est sous la surveillance et avec le consentement de son représentant légal 
              (parent ou tuteur). Néanmoins, nous nous réservons le droit de supprimer tout compte qui 
              appartiendrait manifestement à une personne de moins de 16 ans dès que cela est porté à notre 
              connaissance, et ce sans préavis.
            </p>
            <p>
              Nous ne collectons pas sciemment de données concernant des mineurs de moins de 16 ans. 
              Nous ne proposons pas de produits ou services spécifiquement destinés aux enfants, ni 
              n’effectuons de profilage centré sur les mineurs. Le contenu (aide à la recherche d’emploi, 
              optimisation de CV) s’adresse normalement à des personnes en âge légal de travailler, ce qui 
              en Suisse comme dans l’UE correspond généralement à des personnes de 16 ans et plus (voire 
              18 ans et plus pour la plupart des contrats de travail sans autorisation parentale).
            </p>
            <p>
              Si vous êtes parent ou tuteur et que vous découvrez que votre enfant de moins de 16 ans a créé 
              un compte ou nous a fourni des informations personnelles, veuillez nous contacter 
              immédiatement (section 12) afin que nous prenions les mesures appropriées. Nous 
              procéderons à la suppression des données en question dans les plus brefs délais, à moins que 
              vous ne consentiez explicitement à ce que l’enfant puisse utiliser nos services (dans la limite 
              permise par la loi locale).
            </p>
            <p>
              <strong>Utilisateurs âgés de 16 à 18 ans :</strong> Si vous avez entre 16 et 18 ans, vous êtes autorisé à utiliser 
              CVMatchSwiss dans la mesure où vous êtes en âge de rechercher un emploi (stagiaire, 
              apprenti, jeune diplômé, etc.), mais nous vous recommandons de le faire avec discernement 
              et, si possible, avec l’accompagnement d’un conseiller (scolaire ou parental) pour tirer le 
              meilleur parti de nos conseils. À cet âge, vos données personnelles bénéficient des mêmes 
              protections légales. Si vous avez des questions sur l’utilisation de vos données, n’hésitez pas à 
              les poser.
            </p>
            <p>
              Enfin, nous rappelons qu’en cas de fausse déclaration sur l’âge lors de l’inscription, les 
              conditions générales peuvent prévoir la résiliation du compte. La protection des mineurs est 
              un sujet que nous prenons au sérieux, et nous alignerons nos pratiques sur l’évolution des lois 
              en la matière (par ex. si une « majorité numérique » spécifique devait être introduite par le 
              législateur à l’avenir).
            </p>
          </div>
        </section>

        <section id="contact" className="scroll-mt-24">
          <h3 className="text-lg font-semibold mb-4">12. Contact officiel</h3>
          <div className="space-y-4">
            <p>
              Pour toute question, demande ou préoccupation relative à la présente politique de 
              confidentialité ou au traitement de vos données personnelles, vous pouvez contacter notre 
              responsable à la protection des données (ou l’équipe en charge) aux coordonnées suivantes :
            </p>
            <div className="bg-gray-50 p-6 rounded-lg">
              <h4 className="font-semibold mb-3">P-TRON TECH (CVMatchSwiss) – Service Protection des Données</h4>
              <p className="mb-2">
                Adresse : Baarerstrasse 112, 6300 Zoug, Suisse.
              </p>
              <p className="mb-2">
                E-mail : support@cvmatchswiss.ch
              </p>
              <p className="text-sm text-gray-600">
                Nous privilégions le contact par e-mail pour un traitement plus rapide de vos demandes liées à 
                la vie privée. Vous pouvez écrire en français (ou en anglais, ou en allemand si vous le 
                souhaitez, étant donné le contexte suisse multilingue). Nous vous répondrons généralement 
                dans la langue de votre demande.
              </p>
            </div>
            <p>
              Si vous exercez l’un de vos droits (section 7) par e-mail, pensez à utiliser de préférence 
              l’adresse e-mail associée à votre compte afin de faciliter la vérification de votre identité. Sans 
              cela, nous pourrions être amenés à vous demander des informations additionnelles.
            </p>
            <p>
              <strong>Autre contact (support général) :</strong> Pour les questions d’ordre général sur l’utilisation du service 
              (qui ne seraient pas relatives à vos données personnelles), vous pouvez continuer à utiliser les 
              canaux de support habituels (par ex. notre formulaire de contact sur le site ou l’adresse 
              support@cvmatchswiss.ch si elle existe). Nous tenons à faire la distinction entre les demandes de 
              support classique et les demandes légales sur les données, afin de les traiter avec le niveau 
              d’attention adéquat.
            </p>
            <div className="mt-4">
              <h4 className="font-semibold mb-2">Autorité de protection des données</h4>
              <p>
                Si vous avez une plainte concernant la manière dont nous 
                gérons vos données et que notre réponse ne vous satisfait pas, vous avez le droit de contacter 
                l’autorité de contrôle compétente. En Suisse, l’autorité de référence est le Préposé fédéral à la 
                protection des données et à la transparence (PFPDT), joignable via son site web officiel 
                (https://www.edoeb.admin.ch). Dans l’Union européenne, vous pouvez contacter l’autorité de 
                votre pays (par ex. le CEPDP ou une autorité nationale). Nous espérons toutefois avoir réglé 
                toute question directement avec vous.
              </p>
            </div>
            <p>
              Nous sommes ouverts au dialogue et à l’amélioration continue de nos pratiques. N’hésitez 
              donc pas à nous faire part de vos retours ou suggestions concernant la confidentialité chez 
              CVMatchSwiss.
            </p>
          </div>
        </section>

        <section id="policy-changes" className="scroll-mt-24">
          <h3 className="text-lg font-semibold mb-4">13. Modifications de la déclaration</h3>
          <div className="space-y-4">
            <p>
              La présente politique de confidentialité peut être amenée à évoluer. En effet, nous sommes
              susceptibles de mettre à jour ce document pour refléter les changements dans nos pratiques de
              traitement des données, l’évolution de nos services, ou pour nous conformer à de nouvelles
              exigences légales et réglementaires en matière de protection des données. Nous vous
              encourageons à consulter régulièrement cette page pour rester informé de nos pratiques
              actuelles.
            </p>
            <p>
              En cas de modification majeure, nous nous engageons à vous notifier ces changements via les
              canaux appropriés. Par exemple, nous pourrions afficher un avis bien visible sur notre
              site/app, ou vous envoyer une communication par e-mail à l’adresse associée à votre compte,
              avant que les nouvelles dispositions n’entrent en vigueur. Si la loi l’exige, nous solliciterons
              votre consentement pour tout nouveau traitement qui le nécessiterait.
            </p>
            <p>
              Chaque version de la politique est identifiée par un numéro de version et une date d’entrée en
              vigueur (voir en-tête du document). Ainsi, vous pouvez aisément vérifier si des changements
              ont eu lieu depuis votre dernière lecture. Nous conserverons également les versions
              antérieures de la politique de confidentialité dans nos archives et elles pourront vous être
              communiquées sur demande, afin de garantir la transparence.
            </p>
            <p>
              En cas de désaccord significatif de votre part avec une modification de la politique, et si vous
              estimez que vous ne pouvez pas accepter les nouvelles conditions, vous avez la possibilité de
              cesser d’utiliser nos services et de demander la suppression de vos données (voir section 7 sur
              le droit à l’effacement). Bien que nous espérons que cela ne sera pas nécessaire, la protection
              de vos données étant au cœur de nos valeurs, nous restons à l’écoute de vos préoccupations.
              Nous considérons qu’en continuant à utiliser CVMatchSwiss après l’entrée en vigueur d’une
              version mise à jour de cette politique, vous acceptez les changements effectués.
              Nous vous remercions de votre confiance.
            </p>
          </div>
          <p className='mt-8 text-sm text-gray-600'>Version 1.0 – Dernière mise à jour : 5 septembre 2025</p>
        </section>
      </div>

      {/* Scroll to top button */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 bg-indigo-600 hover:bg-indigo-700 text-white p-3 rounded-full shadow-lg transition-all duration-300 ease-in-out hover:scale-110 z-50"
          aria-label="Scroll to top"
        >
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 10l7-7m0 0l7 7m-7-7v18"
            />
          </svg>
        </button>
      )}
    </div>
  );
};

export default PrivacyPolicy;